import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { convertQuotationToPurchase, getSaleRoundingSettings } from '../api';
import ConfirmModal from '../components/ConfirmModal';
import { previewSaleRounding } from '../utils/saleRounding';
import { notify } from '../utils/alertBus';

const METHODS = [
  ['efectivo', 'Efectivo'], ['tarjeta', 'Tarjeta'], ['transferencia', 'Transferencia'],
  ['cheque', 'Cheque'], ['crédito', 'Crédito'],
];
const money = (value) => `$${formatPesoAmount(Number(value || 0))}`;

export default function useQuotationSaleConversion(branchId) {
  const [draft, setDraft] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const resolver = useRef(null);
  const opening = useRef(false);

  useEffect(() => () => {
    resolver.current?.(null);
    resolver.current = null;
  }, []);

  const requestConversion = useCallback(async (quotationId, totalAmount) => {
    if (opening.current || resolver.current) return null;
    opening.current = true;
    try {
      const response = await getSaleRoundingSettings(branchId);
      return await new Promise((resolve) => {
        resolver.current = resolve;
        setDraft({
          quotationId, totalBeforeRounding: Number(totalAmount || 0),
          settings: response.effective, method: '', creditDueDate: '', error: '',
          serverPreview: null,
        });
      });
    } finally {
      opening.current = false;
    }
  }, [branchId]);

  const close = (response = null) => {
    resolver.current?.(response);
    resolver.current = null;
    setDraft(null);
  };
  const preview = draft ? draft.serverPreview || previewSaleRounding({
    totalAmount: draft.totalBeforeRounding,
    settings: draft.settings,
    checkoutPaymentMethods: draft.method ? [draft.method] : [],
    paymentTerm: draft.method === 'crédito' ? 'credit' : 'immediate',
  }) : null;
  const requiresMethod = Boolean(draft?.settings?.enabled
    && Array.isArray(draft.settings.eligiblePaymentMethods));
  const change = (patch) => setDraft((current) => ({
    ...current, ...patch, error: '', serverPreview: null,
  }));

  const submit = async () => {
    if (submitting || !draft || (requiresMethod && !draft.method)) return;
    if (draft.method === 'crédito' && !draft.creditDueDate) return;
    setSubmitting(true);
    try {
      const payload = { expectedTotalAmount: preview.totalAmount };
      if (draft.method) {
        payload.checkoutPaymentMethods = [draft.method];
        payload.paymentMethod = draft.method;
        payload.paymentTerm = draft.method === 'crédito' ? 'credit' : 'immediate';
        if (draft.method === 'crédito') payload.creditDueDate = draft.creditDueDate;
      }
      const response = await convertQuotationToPurchase(branchId, draft.quotationId, payload);
      if (response?.warning) notify(response.warning);
      close(response);
    } catch (error) {
      const data = error?.response?.data;
      if (data?.code === 'SALE_TOTAL_CHANGED' && data.purchase) {
        const total = Number(data.purchase.totalAmount);
        const discount = Number(data.purchase.roundingDiscountAmount || 0);
        setDraft((current) => ({
          ...current,
          totalBeforeRounding: total + discount,
          serverPreview: { ...data.purchase, totalBeforeRounding: total + discount },
          error: 'El total cambió. Revisa el nuevo total y confirma nuevamente.',
        }));
      } else if (data?.code === 'SALE_PAYMENT_METHOD_REQUIRED') {
        try {
          const response = await getSaleRoundingSettings(branchId);
          setDraft((current) => ({
            ...current, settings: response.effective, serverPreview: null,
            error: 'La configuración cambió. Selecciona un método de pago y revisa el total.',
          }));
        } catch (refreshError) {
          setDraft((current) => ({ ...current, error: 'No se pudo actualizar la configuración. Cancela e intenta nuevamente.' }));
        }
      } else {
        setDraft((current) => ({
          ...current, error: data?.message || 'No se pudo convertir la cotización. Intenta nuevamente.',
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const conversionDialog = draft ? (
    <ConfirmModal
      isOpen
      title="Convertir cotización"
      message="Se creará una venta sin pagar con este total."
      confirmLabel={submitting ? 'Convirtiendo…' : 'Crear venta'}
      confirmDisabled={submitting || (requiresMethod && !draft.method)
        || (draft.method === 'crédito' && !draft.creditDueDate)}
      onCancel={() => { if (!submitting) close(); }}
      onConfirm={submit}
    >
      <div className="mt-4 space-y-3">
        {requiresMethod && (
          <label className="block text-sm text-gray-700">
            Método de pago de la venta
            <select
              className="mt-1 w-full rounded border border-gray-300 p-2"
              value={draft.method}
              disabled={submitting}
              onChange={(event) => change({ method: event.target.value })}
            >
              <option value="">Selecciona un método</option>
              {METHODS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        )}
        {draft.method === 'crédito' && (
          <label className="block text-sm text-gray-700">
            Vencimiento del crédito
            <input type="date" className="mt-1 w-full rounded border border-gray-300 p-2"
              value={draft.creditDueDate} disabled={submitting}
              onChange={(event) => change({ creditDueDate: event.target.value })} />
          </label>
        )}
        <dl className="space-y-1 text-sm">
          <div className="flex justify-between"><dt>Total antes del redondeo</dt><dd>{money(preview.totalBeforeRounding)}</dd></div>
          {preview.roundingDiscountAmount > 0 && <div className="flex justify-between text-green-700"><dt>Descuento por redondeo</dt><dd>−{money(preview.roundingDiscountAmount)}</dd></div>}
          <div className="flex justify-between text-base font-semibold"><dt>Total de la venta</dt><dd>{money(preview.totalAmount)}</dd></div>
        </dl>
        {draft.error && <p role="alert" className="text-sm text-red-700">{draft.error}</p>}
      </div>
    </ConfirmModal>
  ) : null;

  return { requestConversion, conversionDialog };
}
